/**
 * Shared utility functions for managing variants across Optimize and Personalize flows
 */

export type VariantStatus = 'pending' | 'live' | 'approved' | 'dropped'
export type PersonalizedVariantStatus =
  | 'approved'
  | 'pending'
  | 'rejected'
  | 'live'
  | 'dropped'
  | 'replaced'

export type Variant = {
  _id?: string
  variantId?: string
  variant_id?: string
  text?: string
  control?: boolean
  bandit_status?: { status: VariantStatus | PersonalizedVariantStatus }
  status?: VariantStatus | PersonalizedVariantStatus
  variantStatus?: VariantStatus | PersonalizedVariantStatus
  [key: string]: any // Allow additional properties for flexibility
}

/**
 * Get the status of a variant from various possible status fields
 */
export const getVariantStatus = (
  variant: Variant
): VariantStatus | PersonalizedVariantStatus | undefined => {
  return (
    variant.bandit_status?.status || variant.status || variant.variantStatus
  )
}

export const filterByStatus = (
  variant: Variant,
  statusFilter: VariantStatus
): boolean => {
  const status = getVariantStatus(variant)

  if (statusFilter === 'pending') {
    return status === 'pending'
  } else if (statusFilter === 'live') {
    return status === 'live' || status === 'approved'
  } else if (statusFilter === 'approved') {
    return status === 'approved'
  } else if (statusFilter === 'dropped') {
    return status === 'dropped'
  }

  return false
}

export const getVariantId = (variant: Variant): string => {
  return variant._id || variant.variantId || variant.variant_id || ''
}

export const calculateStatusCounts = (
  variants: Variant[]
): {
  pending: number
  live: number
  dropped: number
} => {
  const counts = {
    pending: 0,
    live: 0,
    dropped: 0,
  }

  variants.forEach((variant) => {
    const status = getVariantStatus(variant)
    if (status === 'pending') {
      counts.pending++
    } else if (status === 'live' || status === 'approved') {
      counts.live++
    } else if (status === 'dropped') {
      counts.dropped++
    }
  })

  return counts
}
